import { Button } from "@components/common/button"
import { DictType } from "@pma/types"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@components/common/alert-dialog"
import axios from "axios"
import { parseCookies } from "nookies"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "@hooks/use-toast"
import { ACCESS_TOKEN } from "@pma/utils/constants"
import { SERVER_URL, WORK_DIARY_DELETE_ENDPOINT } from "@pma/utils/api-routes"
import { Trash2Icon } from "lucide-react"
import { Tooltip, TooltipContent, TooltipTrigger } from "@components/common/tooltip"
import { HTMLAttributes } from "react"
import useTranslator from "@hooks/use-translator"

interface Props extends HTMLAttributes<HTMLDivElement> {
  workDiaries: DictType
}

const DeleteWorkDiaries = ({ workDiaries, children, className }: Props) => {
  const accessToken = parseCookies()[ACCESS_TOKEN]
  const queryClient = useQueryClient()

  const { translate } = useTranslator()

  const deleteWorkDiaries = async () => {
    const response = await axios.delete(
      SERVER_URL + WORK_DIARY_DELETE_ENDPOINT + `/${workDiaries?.id}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )

    return response.data
  }

  const deleteWorkDiariesMutation = useMutation({
    mutationKey: ["delete-work-diaries", workDiaries?.id],
    mutationFn: deleteWorkDiaries,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["work-diaries-context"] })
      toast({
        title: translate("Success"),
        description: `${workDiaries?.number} ${translate("has been deleted")}`,
        variant: "success",
      })
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message
      toast({
        title: translate("Error"),
        description: errorMessage ||
          `${translate("Unable to delete")} ${workDiaries?.number} ${translate("Work Diaries")}`,
        variant: "error",
      })
    },
  })

  return (
    <AlertDialog>
      <AlertDialogTrigger className={className}>
        <Tooltip>
          <TooltipTrigger>
            {children ? (
              children
            ) : (
              <Button variant={"ghost"} className='text-xs text-red-500' size={"icon"}>
                <Trash2Icon size={14} />
              </Button>
            )}
          </TooltipTrigger>
          <TooltipContent className='flex items-center gap-1 bg-destructive'>
            <h4>
              {translate(`Delete`)} {workDiaries?.title}
            </h4>
          </TooltipContent>
        </Tooltip>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {translate("Proceed to delete")} {workDiaries?.title}?
          </AlertDialogTitle>
          <AlertDialogDescription>
            {translate("This action cannot be undone")}.{" "}
            {translate("This will permanently delete the work diaries")}.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{translate("Cancel")}</AlertDialogCancel>
          <AlertDialogAction
            className='bg-destructive'
            disabled={deleteWorkDiariesMutation?.isPending}
            onClick={() => deleteWorkDiariesMutation.mutate()}
          >
            {translate("Continue")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

export default DeleteWorkDiaries
